'use client';

import { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { ArrowRight, Check, Server } from 'lucide-react';
import { SiteNav } from '@/components/SiteNav';
import { Reveal, MarketingFooter } from '@/components/marketing';

// Modulo di richiesta della licenza. Non c'e' pagamento e non c'e' registrazione:
// il software gira sul server del cliente, quindi prima si parla, si dimensiona
// l'installazione e poi si fattura. La route /api/licence-request salva su Convex
// e manda due mail (a noi e a chi ha compilato).

type Field = { name: string; required: boolean; type: 'text' | 'email' | 'tel' | 'textarea' };

const FIELDS: Field[] = [
  { name: 'company', required: true, type: 'text' },
  { name: 'contact', required: true, type: 'text' },
  { name: 'email', required: true, type: 'email' },
  { name: 'phone', required: false, type: 'tel' },
  { name: 'country', required: false, type: 'text' },
  { name: 'slabs', required: false, type: 'text' },
  { name: 'notes', required: false, type: 'textarea' },
];

const INPUT_CLASS =
  'w-full rounded-[10px] border border-stone-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-[11px] text-[15px] outline-none transition-colors focus:border-[var(--accent)] placeholder:text-stone-400 dark:placeholder:text-zinc-600';

export default function LicenceContent() {
  const t = useTranslations('Licence');
  const locale = useLocale();
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (state === 'sending') return;
    setState('sending');

    const data = Object.fromEntries(new FormData(event.currentTarget).entries());
    try {
      const res = await fetch('/api/licence-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, locale }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setState('sent');
    } catch {
      setState('error');
    }
  }

  return (
    <div className="flex flex-col bg-stone-50 dark:bg-zinc-950 text-stone-950 dark:text-zinc-50">
      <SiteNav />

      <section style={{ padding: 'clamp(56px,8vw,104px) 0 clamp(40px,5vw,72px)' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-10 lg:gap-16 items-start">

            {/* Colonna sinistra: cosa e' la licenza */}
            <Reveal>
              <div
                className="w-10 h-10 rounded-[10px] flex items-center justify-center mb-5"
                style={{ background: 'var(--accent-soft)' }}
              >
                <Server size={19} style={{ color: 'var(--accent)' }} />
              </div>
              <div
                className="font-mono text-[12px] font-medium tracking-[0.14em] uppercase mb-4"
                style={{ color: 'var(--accent)' }}
              >
                {t('eyebrow')}
              </div>
              <h1
                className="font-black tracking-[-0.035em] leading-[1.06] m-0 mb-5"
                style={{ fontSize: 'clamp(34px,4.5vw,52px)' }}
              >
                {t('title')}
              </h1>
              <p className="text-[17px] leading-[1.6] text-stone-500 dark:text-zinc-400 m-0 mb-7 max-w-[46ch]">
                {t('body')}
              </p>

              <ul className="flex flex-col gap-3 m-0 p-0 list-none mb-7">
                {(['p1', 'p2', 'p3', 'p4'] as const).map((p) => (
                  <li key={p} className="flex items-start gap-3 text-[14.5px] leading-[1.55]">
                    <Check size={16} className="mt-[3px] shrink-0" style={{ color: 'var(--accent)' }} />
                    <span className="text-stone-600 dark:text-zinc-300">{t(p)}</span>
                  </li>
                ))}
              </ul>

              <div className="rounded-[14px] border border-stone-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
                <div className="text-[26px] font-black tracking-tight leading-none mb-1.5">{t('price')}</div>
                <p className="text-[13.5px] leading-[1.55] text-stone-500 dark:text-zinc-400 m-0">
                  {t('priceNote')}
                </p>
              </div>

              <p className="text-[13px] text-stone-400 dark:text-zinc-500 mt-5 m-0">
                {t('altIntro')}{' '}
                <Link href="/signup" className="underline hover:text-stone-600 dark:hover:text-zinc-300">
                  {t('altLink')}
                </Link>
              </p>
            </Reveal>

            {/* Colonna destra: il modulo */}
            <Reveal>
              <div className="rounded-[18px] border border-stone-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-7">
                {state === 'sent' ? (
                  <div className="flex flex-col items-start gap-3 py-6">
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center"
                      style={{ background: 'var(--accent-soft)' }}
                    >
                      <Check size={22} style={{ color: 'var(--accent)' }} />
                    </div>
                    <h2 className="text-[20px] font-bold tracking-tight m-0">{t('sentTitle')}</h2>
                    <p className="text-[14.5px] leading-[1.6] text-stone-500 dark:text-zinc-400 m-0">
                      {t('sentBody')}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="flex flex-col gap-4">
                    <h2 className="text-[19px] font-bold tracking-tight m-0">{t('formTitle')}</h2>

                    {/* Campo esca per i bot: nascosto agli occhi e ai lettori di schermo. */}
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      className="hidden"
                    />

                    {FIELDS.map(({ name, required, type }) => (
                      <label key={name} className="flex flex-col gap-1.5">
                        <span className="text-[13px] font-medium text-stone-600 dark:text-zinc-300">
                          {t(`f_${name}` as 'f_company')}
                          {required && <span style={{ color: 'var(--accent)' }}> *</span>}
                        </span>
                        {type === 'textarea' ? (
                          <textarea
                            name={name}
                            rows={4}
                            className={`${INPUT_CLASS} resize-y`}
                            placeholder={t(`ph_${name}` as 'ph_notes')}
                          />
                        ) : (
                          <input
                            name={name}
                            type={type}
                            required={required}
                            className={INPUT_CLASS}
                            placeholder={t(`ph_${name}` as 'ph_company')}
                          />
                        )}
                      </label>
                    ))}

                    {state === 'error' && (
                      <p className="text-[13.5px] text-red-600 dark:text-red-400 m-0">{t('errorBody')}</p>
                    )}

                    <button
                      type="submit"
                      disabled={state === 'sending'}
                      className="inline-flex items-center justify-center gap-2 px-6 py-[13px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity disabled:opacity-60 mt-1"
                    >
                      {state === 'sending' ? t('sending') : t('submit')}
                      {state !== 'sending' && <ArrowRight size={15} />}
                    </button>

                    <p className="text-[12.5px] leading-[1.55] text-stone-400 dark:text-zinc-500 m-0">
                      {t('formNote')}
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
